import {useEffect} from 'react';
import {isEqual} from 'lodash';

import KpiAggregatedChartWidgetConfigContent from './KpiAggregatedChartWidgetConfigContent';
import {KpiAggregatedChartWidgetConfigProps, KpiAggregatedChartWidgetConfigSettings} from '../types';
import {isKpiWidgetItemValid, KpiGroupBy, updatedKpiItem, validateKpiWidgetTimeConfig} from '../../common/kpiUtils';
import ResourcesProvider from '../../common/ResourcesProvider';
import './KpiAggregatedChartWidgetConfig.scss';

export const fixBackwardsCompatibility = (settings: any): KpiAggregatedChartWidgetConfigSettings => ({
	items: Array.isArray(settings.items) ? settings.items.filter(isKpiWidgetItemValid) : [],
	unit: settings.unit || '',
	stacked: settings.stacked || false,
	commonYAxis: settings.commonYAxis || false,
	aggregationType: settings.aggregationType || 'QUERY_AVERAGE',
	groupBy: settings.groupBy || 'BY_DAY',
	periodTimeframe: settings.periodTimeframe || 'CURRENT',
	periodType: Object.keys(KpiGroupBy).includes(settings.periodType)
		? settings.periodType
		: Object.keys(KpiGroupBy)[0],
	periodCount:
		typeof settings.periodCount === 'string'
			? parseInt(settings.periodCount)
			: typeof settings.periodCount === 'number'
			? settings.periodCount
			: 1,
	customPeriodFrom: settings.customPeriodFrom,
	customPeriodTo: settings.customPeriodTo,
	workingShiftEnabled: settings.workingShiftEnabled || false,
	workingShiftDays: settings.workingShiftDays,
	workingShiftTimeFrom: settings.workingShiftTimeFrom,
	workingShiftTimeTo: settings.workingShiftTimeTo
});

const KpiAggregatedChartWidgetConfig = ({
	config,
	context,
	onChangeConfig,
	setApplyButtonDisabled
}: KpiAggregatedChartWidgetConfigProps) => {
	const {
		items,
		periodTimeframe,
		periodType,
		workingShiftEnabled,
		workingShiftDays,
		workingShiftTimeFrom,
		workingShiftTimeTo
	} = config.settings;
	useEffect(() => {
		const fixedSettings = fixBackwardsCompatibility(config.settings);

		if (!isEqual(config.settings, fixedSettings)) {
			onChangeConfig({...config, settings: fixedSettings});
		} // this effect should happen only once
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	useEffect(() => {
		const valid1 = items.every(isKpiWidgetItemValid);
		const valid2 = validateKpiWidgetTimeConfig(
			periodTimeframe,
			periodType,
			workingShiftEnabled,
			workingShiftDays,
			workingShiftTimeFrom,
			workingShiftTimeTo
		);
		setApplyButtonDisabled(!valid1 || !valid2);
	}, [
		items,
		periodTimeframe,
		periodType,
		workingShiftEnabled,
		workingShiftDays,
		workingShiftTimeFrom,
		workingShiftTimeTo,
		setApplyButtonDisabled
	]);
	return (
		<ResourcesProvider
			assetGroup={context.type === 'assetGroup' ? context.assetGroup : null}
			onAssetSelect={(asset, index) => {
				const newItems = updatedKpiItem(config.settings.items, index, {
					assetUrn: asset.urn,
					kpiName: undefined
				});
				onChangeConfig({...config, settings: {...config.settings, items: newItems}});
			}}
			onKpiNameSelect={(kpiName, index) => {
				const newItems = updatedKpiItem(config.settings.items, index, {
					kpiName
				});
				onChangeConfig({...config, settings: {...config.settings, items: newItems}});
			}}>
			{(setAssetSelecting, setKpiNameSelecting) => (
				<KpiAggregatedChartWidgetConfigContent
					context={context}
					config={config}
					onChangeConfig={onChangeConfig}
					setSelectingAsset={setAssetSelecting}
					setSelectingKpi={setKpiNameSelecting}
				/>
			)}
		</ResourcesProvider>
	);
};

export default KpiAggregatedChartWidgetConfig;
